// Photo Mode — full-screen National Geographic style gallery of photoreal
// photographs of the vivarium ecosystem. Auto-advances, Ken Burns motion.
import React, { useCallback, useEffect, useRef, useState } from "react";

const SHOTS = [
  {
    src: "/assets/vivarium/photo/photo-day.png",
    title: "The Enclosure at Golden Hour",
    caption: "Desert tortoise and roadrunner share the sand beneath date palms — late-afternoon sun through the glass.",
  },
  {
    src: "/assets/vivarium/photo/photo-night.png",
    title: "Moonlight Shift",
    caption: "After dark the enclosure cools to the 70s; the pool holds the last light of the grow lamps.",
  },
  {
    src: "/assets/vivarium/photo/photo-tortoise.png",
    title: "At the Water's Edge",
    caption: "A Mojave desert tortoise drinks while a flame skimmer patrols the surface of the pool.",
  },
  {
    src: "/assets/vivarium/photo/photo-canopy.png",
    title: "Under the Date Palm",
    caption: "Backlit fronds and hanging date clusters; a chuckwalla basks on warm stone below.",
  },
  {
    src: "/assets/vivarium/photo/photo-macro.png",
    title: "The Smallest Resident",
    caption: "Flame skimmer dragonfly at rest on prickly pear — the enclosure's aerial insectivore.",
  },
];

export function PhotoMode({ onClose }) {
  const [idx, setIdx] = useState(0);
  const [alive, setAlive] = useState(() => SHOTS.map(() => true));
  const timer = useRef(null);

  const live = SHOTS.filter((_, i) => alive[i]);
  const cur = Math.min(idx, Math.max(live.length - 1, 0));

  const next = useCallback(() => setIdx((i) => (i + 1) % Math.max(live.length, 1)), [live.length]);

  useEffect(() => {
    timer.current = setInterval(next, 6500);
    return () => clearInterval(timer.current);
  }, [next]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") setIdx((i) => (i - 1 + Math.max(live.length, 1)) % Math.max(live.length, 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, onClose, live.length]);

  if (!live.length) return null;
  const shot = live[cur];

  return (
    <div className="absolute inset-0 z-40 bg-black flex flex-col" data-testid="photo-mode">
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {live.map((s, i) => (
          <img
            key={s.src}
            src={s.src}
            alt={s.title}
            onError={() => setAlive((a) => a.map((v, j) => (SHOTS[j] === s ? false : v)))}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === cur ? "opacity-100 growlab-kenburns" : "opacity-0"}`}
            data-testid={i === cur ? "photo-mode-image" : undefined}
          />
        ))}
        <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-black/95 via-black/50 to-transparent pointer-events-none" />
        <div className="absolute left-4 right-4 bottom-16 sm:bottom-14 pointer-events-none">
          <div className="text-[9px] font-mono tracking-[0.3em] text-amber-400/90 uppercase mb-1" data-testid="photo-mode-credit">
            BGNZN FIELD ARCHIVE · FRAME {String(cur + 1).padStart(2, "0")} / {String(live.length).padStart(2, "0")}
          </div>
          <h2 className="text-2xl sm:text-4xl font-extrabold text-stone-50" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }} data-testid="photo-mode-title">
            {shot.title}
          </h2>
          <p className="text-xs sm:text-sm text-stone-300 mt-1 max-w-xl">{shot.caption}</p>
        </div>
        <button
          data-testid="photo-mode-close"
          onClick={onClose}
          className="absolute top-3 right-3 px-4 py-2 rounded-lg bg-black/60 backdrop-blur border border-amber-500/40 text-amber-200 font-mono text-[11px] tracking-widest min-h-[44px] hover:border-amber-300"
        >
          ✕ 3D MODEL
        </button>
      </div>
      <div className="flex items-center justify-center gap-3 px-4 py-3 bg-black border-t border-amber-500/20">
        <button
          data-testid="photo-mode-prev"
          onClick={() => setIdx((i) => (i - 1 + live.length) % live.length)}
          className="px-4 py-2 rounded-lg bg-stone-950 border border-slate-700/60 text-stone-200 font-mono text-[11px] tracking-widest min-h-[44px]"
        >
          ◀ PREV
        </button>
        <div className="flex items-center gap-1.5" data-testid="photo-mode-dots">
          {live.map((s, i) => (
            <button
              key={s.src}
              data-testid={`photo-dot-${i}`}
              onClick={() => setIdx(i)}
              className={`w-2.5 h-2.5 rounded-full transition-colors ${i === cur ? "bg-amber-400" : "bg-stone-700"}`}
            />
          ))}
        </div>
        <button
          data-testid="photo-mode-next"
          onClick={next}
          className="px-4 py-2 rounded-lg bg-amber-400 border border-amber-300 text-stone-950 font-mono text-[11px] tracking-widest min-h-[44px]"
        >
          NEXT ▶
        </button>
      </div>
    </div>
  );
}
